import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { usePlayer } from "@/lib/player";
import { Play, Users, MapPin, Calendar, Coins, Globe2, Radio } from "lucide-react";
import NotificationToggle from "@/components/NotificationToggle";
import ReportButton from "@/components/ReportButton";
import ShareStationButton from "@/components/ShareStationButton";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const resolveImg = (u) => !u ? "" : (u.startsWith("http") ? u : `${BACKEND_URL}${u}`);

const DAYS = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];

export default function StationDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const { tuneIn, station: playing } = usePlayer();

  useEffect(() => {
    (async () => {
      const r = await api.get(`/stations/${id}`);
      setData(r.data);
    })();
  }, [id]);

  if (!data) return <div className="max-w-7xl mx-auto px-5 py-20 text-slate-500 fl-mono text-xs tracking-widest uppercase">// Loading frequency…</div>;
  const s = data.station;
  const isPlaying = playing?.station_id === s.station_id;

  return (
    <main className="pb-40">
      {/* Banner */}
      <section className="relative h-72 sm:h-96 overflow-hidden">
        {s.cover_url ? <img src={resolveImg(s.cover_url)} alt="" className="w-full h-full object-cover object-top" /> : <div className="w-full h-full bg-zinc-900" />}
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-black/60 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 max-w-7xl mx-auto px-5 pb-8">
          <div className="flex items-center gap-2 fl-mono text-[11px] tracking-[0.3em] uppercase text-emerald-500"><span className="live-dot" /> Live · {s.genre}</div>
          <h1 className="fl-display text-5xl sm:text-7xl font-extrabold tracking-tighter mt-3">{s.name}</h1>
          {s.tagline && <p className="mt-3 max-w-2xl text-slate-300">{s.tagline}</p>}
          <div className="mt-4 flex flex-wrap items-center gap-3 text-xs fl-mono uppercase tracking-widest text-slate-300">
            <span className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-emerald-500" /> {s.country}</span>
            <span className="flex items-center gap-1.5"><Globe2 className="w-3.5 h-3.5 text-emerald-500" /> {s.language}</span>
            <span className="flex items-center gap-1.5"><Users className="w-3.5 h-3.5 text-emerald-500" /> {s.listener_count || 0} listening</span>
            <span className="px-2 py-0.5 border border-white/15 rounded-full">Age {s.age_target}</span>
          </div>
          <div className="mt-6 flex flex-wrap gap-3">
            <button onClick={() => tuneIn(s)} className="fl-btn" data-testid="station-tune-in">
              <Play className="w-4 h-4" /> {isPlaying ? "Now playing" : "Tune in"}
            </button>
            {s.tip_jar_url && (
              <a href={s.tip_jar_url} target="_blank" rel="noreferrer" className="fl-btn ghost" data-testid="station-tip-jar">
                <Coins className="w-4 h-4 text-emerald-500" /> Tip the host
              </a>
            )}
            <NotificationToggle stationId={s.station_id} />
            <ShareStationButton station={s} />
            <ReportButton stationId={s.station_id} />
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 mt-12 grid lg:grid-cols-3 gap-8">
        {/* Playlist */}
        <div className="lg:col-span-2">
          <h2 className="fl-display text-2xl font-bold tracking-tight mb-4">Playlist rotation</h2>
          <div className="fl-card divide-y divide-white/5">
            {data.tracks.length === 0 ? (
              <div className="p-6 text-slate-500 fl-mono text-xs uppercase tracking-widest">// No tracks yet</div>
            ) : data.tracks.map((t, i) => (
              <div key={t.track_id} className="flex items-center gap-4 p-4">
                <span className="fl-mono text-xs text-slate-500 w-6">{String(i+1).padStart(2,"0")}</span>
                <div className="w-10 h-10 rounded bg-zinc-800 overflow-hidden shrink-0">
                  {t.cover_url ? <img src={resolveImg(t.cover_url)} alt="" className="w-full h-full object-cover object-top" /> : <Radio className="w-5 h-5 m-auto mt-2.5 text-emerald-500" />}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-white truncate">{t.title}</div>
                  <div className="text-xs text-slate-400 truncate">{t.artist}</div>
                </div>
                <span className="fl-mono text-[10px] uppercase tracking-widest text-slate-500">{t.kind}</span>
                <span className="fl-mono text-xs text-slate-400">{fmt(t.duration)}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Schedule */}
        <div>
          <h2 className="fl-display text-2xl font-bold tracking-tight mb-4"><Calendar className="inline w-5 h-5 mr-2 text-emerald-500" />Weekly schedule</h2>
          <div className="fl-card p-4 space-y-3">
            {data.shows.length === 0 ? (
              <div className="text-slate-500 fl-mono text-xs uppercase tracking-widest">// No shows scheduled</div>
            ) : data.shows.map(sh => (
              <div key={sh.show_id} className="flex items-start gap-3 p-3 border border-white/5 rounded-lg">
                <div className="text-center">
                  <div className="fl-mono text-[10px] tracking-widest uppercase text-emerald-500">{DAYS[sh.day_of_week]}</div>
                  <div className="fl-display text-xl font-bold leading-none mt-1">{String(sh.start_hour).padStart(2,"0")}:00</div>
                </div>
                <div className="min-w-0">
                  <div className="text-white font-medium truncate">{sh.title}</div>
                  <div className="text-xs text-slate-400">Hosted by {sh.host} · {sh.duration_min} min</div>
                  {sh.description && <div className="text-xs text-slate-500 mt-1 line-clamp-2">{sh.description}</div>}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}

function fmt(sec) {
  const s = Math.round(sec || 0);
  const m = Math.floor(s/60), r = s%60;
  return `${m}:${String(r).padStart(2,"0")}`;
}
